import {
  Alert,
  AlertIcon,
  Box,
  Card,
  CardBody,
  Flex,
  Heading,
  Link,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'urql';

import {
  COMPOSITION_ENTITY_DATA,
  PositionsEntityKind,
} from '@revelio/data-access';

import DashboardPage from '../DashboardPage';

const ENTITY_TYPES = Object.values(PositionsEntityKind);

const BackToSearch = () => {
  return (
    <Link href="/tools/find" color="text.primary">
      Back to Search
    </Link>
  );
};

const EntityCard = ({
  entityType,
  entityId,
}: {
  entityType: PositionsEntityKind;
  entityId: number;
}) => {
  const [result] = useQuery({
    query: COMPOSITION_ENTITY_DATA,
    variables: {
      entities: [{ EntType: entityType, EntID: entityId }],
    },
    context: useMemo(
      () => ({
        skipGlobalErrorHandling: true,
      }),
      []
    ),
  });

  const entityData = result.data?.composition_v2?.entities?.[0];

  if (result.fetching) {
    return (
      <Card>
        <CardBody>
          <Box display="flex" alignItems="center" gap={2}>
            <Spinner size="sm" />
            <Text>Loading entity {entityId}...</Text>
          </Box>
        </CardBody>
      </Card>
    );
  }

  if (result.error) {
    return (
      <Card>
        <CardBody>
          <Alert status="error">
            <AlertIcon />
            Error loading entity {entityId}: {result.error.message}
          </Alert>
        </CardBody>
      </Card>
    );
  }

  if (!entityData) {
    return (
      <Card>
        <CardBody>
          <Alert status="warning">
            <AlertIcon />
            No entity found for {entityType} with ID {entityId}
          </Alert>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardBody>
        <VStack align="start" spacing={2}>
          <Heading color="text.primary" size="sm">
            {entityData.metadata.long_name}
          </Heading>
          <Text>
            <strong>ID:</strong> {entityData.metadata.id}
          </Text>
          <Text>
            <strong>Short Name:</strong> {entityData.metadata.short_name}
          </Text>
          <Text>
            <strong>Long Name:</strong> {entityData.metadata.long_name}
          </Text>
          <Text>
            <strong>Type:</strong> {entityData.metadata.type}
          </Text>
        </VStack>
      </CardBody>
    </Card>
  );
};

export const EntitySearchResults = () => {
  const { entityType, id } = useParams<{
    entityType: PositionsEntityKind;
    id: string;
  }>();

  const isValidEntityType = entityType && ENTITY_TYPES.includes(entityType);

  // Parse multiple entity IDs from comma-separated string
  const entityIds = useMemo(() => {
    if (!id) return [];
    return (
      id
        .split(',')
        .map((idStr) => idStr.trim())
        // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
        .filter((idStr) => idStr && !isNaN(Number(idStr)))
        // biome-ignore lint/correctness/useParseIntRadix: migrated from develop
        .map((idStr) => parseInt(idStr))
    );
  }, [id]);

  if (!isValidEntityType || entityIds.length === 0) {
    return (
      <DashboardPage title="Entity Search Results" backPath="/tools/find">
        <Card>
          <CardBody>
            <Alert status="warning">
              <AlertIcon />
              Invalid entity type or no valid entity IDs provided.
            </Alert>
          </CardBody>
        </Card>
      </DashboardPage>
    );
  }

  return (
    <DashboardPage title="Entity Details" backPath="/tools/find">
      <Box
        height="100%"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <Flex
          justifyContent="space-between"
          alignItems="center"
          mb={4}
          flexShrink={0}
        >
          <Heading color="text.primary">
            {`Searching ${entityIds.length} Entities`}
          </Heading>
          <BackToSearch />
        </Flex>

        <Box flex="1" overflow="auto" pr={2}>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4} pb={4}>
            {entityIds.map((entityId) => (
              <EntityCard
                key={entityId}
                entityType={entityType}
                entityId={entityId}
              />
            ))}
          </SimpleGrid>
        </Box>
      </Box>
    </DashboardPage>
  );
};
